import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowUpRight } from "lucide-react";

const PLANS = [
  {
    name: "Starter Growth Plan",
    tagline: "Ideal for startups and small businesses building their first growth engine.",
    color: "saavi-green",
    features: [
      "1 Paid channel setup (Meta or Google)",
      "Up to 4 creatives / month",
      "Conversion tracking setup",
      "Basic SEO audit",
      "Monthly performance report",
      "Bi-weekly strategy call",
      "Dedicated account manager",
    ],
    note: "Ad spend excluded",
    featured: false,
  },
  {
    name: "Business Growth Plan",
    tagline: "For growing businesses looking for consistent lead generation across channels.",
    color: "saavi-blue",
    features: [
      "Multi-channel paid campaigns",
      "Up to 12 creatives + 4 reels / month",
      "Full SEO program (technical + content)",
      "Email + WhatsApp automation",
      "Landing page creation",
      "Bi-weekly strategy + monthly review",
      "Dedicated account manager",
      "Custom dashboard reporting",
    ],
    note: "Ad spend excluded",
    featured: true,
  },
  {
    name: "Scale-Up Growth Plan",
    tagline: "Complete growth marketing — automation, SEO, content, and performance combined.",
    color: "saavi-red",
    features: [
      "Full-funnel paid across all channels",
      "Unlimited creatives & video reels",
      "Advanced SEO + AI search optimization",
      "Full marketing automation & CRM",
      "Brand identity + video production",
      "Weekly strategy + executive reviews",
      "Senior account director",
      "Real-time attribution dashboards",
    ],
    note: "Ad spend excluded",
    featured: false,
  },
];

const INCLUDED = [
  "Monthly Deliverables",
  "Ad Spend Excluded",
  "Reporting Frequency",
  "Dedicated Account Manager",
];

export default function Packages() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="Packages"
        title={<>Growth plans built for <span className="text-saavi-blue">every stage.</span></>}
        description="Transparent, deliverable-based packages — pick a plan that matches your growth ambitions."
      />

      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} variant="fade-up" delay={i * 120}>
                <div
                  data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative card-hover lift-on-hover ai-card border rounded-md p-8 bg-card flex flex-col h-full ${
                    p.featured ? `border-${p.color} border-2 lg:-mt-4 lg:mb-4` : "border-border"
                  }`}
                >
                {p.featured && (
                  <span className={`absolute -top-3 left-8 font-mono-overline px-3 py-1 rounded-full bg-${p.color} text-white`}>
                    Most Popular
                  </span>
                )}
                <div className={`font-mono-overline text-${p.color} mb-3`}>{p.name.split(" ")[0]}</div>
                <h3 className="text-2xl md:text-3xl">{p.name}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.tagline}</p>

                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 text-${p.color}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-4">* {p.note}</p>
                  <Link to="/contact">
                    <Button
                      data-testid={`plan-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`w-full h-11 ${p.featured ? "" : ""}`}
                      variant={p.featured ? "default" : "outline"}
                    >
                      Get a Quote <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi">
          <div className="font-mono-overline text-muted-foreground mb-4">Every plan includes</div>
          <h2 className="text-3xl md:text-4xl max-w-2xl mb-10">What's standard across every package.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INCLUDED.map((i) => (
              <div key={i} className="border border-border rounded-md p-5 bg-card">
                <Check className="h-5 w-5 text-saavi-green" />
                <p className="mt-3 text-sm font-semibold">{i}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
